// Hub sign-up hand-off ("Continue with Navo24 account"), behind the VITE_HUB_SIGNUP switch.
//
// The hub (trackingmcp.com) is the single door of record for sign-up: its form carries the consent
// checkbox, captcha and domain check, and records the source/intent. When the switch is "1" this site's
// "Sign up" entry sends the browser to `https://trackingmcp.com/auth/signup?intent=<product>&next=<hand-off>`
// instead of showing the local form; the hub returns the person through `/auth/hand-off?return=<this page>`.
// Switch OFF (the default, anything but "1") means nothing here is used and the local sign-up is unchanged.
// Sign-IN (password, OAuth, magic link) never goes through this file.
//
// The return URL is this site's own https family URL, path + query only: no fragment, no token, no port.
// The flag is read in ONE place (hubSignupEnabled).

export const HUB_SIGNUP_URL = 'https://trackingmcp.com/auth/signup'
/** The hub's product key for this site (hub signup-intent.ts PRODUCT_INTENTS). */
export const HUB_SIGNUP_INTENT = 'aircargo'

/** Exact own hosts the hub accepts as `return` (https, no port). */
const OWN_HOSTS: ReadonlySet<string> = new Set(
  ['aircargomcp.com', 'airratesmcp.com'].flatMap((h) => [h, `www.${h}`]),
)

// Query params that must never be copied into a URL we send to another origin.
const SENSITIVE_PARAMS = ['access_token', 'refresh_token', 'token', 'token_hash', 'sso', 'code']

/** The single read of the switch. Only the literal "1" turns it on. */
export function hubSignupEnabled(raw: unknown = import.meta.env.VITE_HUB_SIGNUP): boolean {
  return raw === '1'
}

/**
 * This site's own return URL: https origin + path + query, minus sensitive params, no fragment.
 * Null for anything that is not an exact own host (lookalikes, other family hosts, http, ports, userinfo).
 */
export function ownReturnUrl(href: string): string | null {
  // biome-ignore lint/suspicious/noControlCharactersInRegex: rejecting control characters is the point
  if (!href || href.length > 1500 || /[\\\s\u0000-\u001f\u007f]/.test(href)) return null
  let u: URL
  try {
    u = new URL(href)
  } catch {
    return null
  }
  if (u.protocol !== 'https:' || u.port || u.username || u.password) return null
  if (!OWN_HOSTS.has(u.hostname)) return null
  for (const k of SENSITIVE_PARAMS) u.searchParams.delete(k)
  const qs = u.searchParams.toString()
  return `https://${u.hostname}${u.pathname}${qs ? `?${qs}` : ''}`
}

/**
 * The hub sign-up URL, or null when the switch is off or the return URL is not this site's own https
 * URL. `enabled` exists for tests; production callers omit it.
 */
export function hubSignupUrl(
  { intent = HUB_SIGNUP_INTENT, returnUrl }: { intent?: string; returnUrl: string },
  enabled: boolean = hubSignupEnabled(),
): string | null {
  if (!enabled) return null
  if (intent !== HUB_SIGNUP_INTENT) return null
  const ret = ownReturnUrl(returnUrl)
  if (!ret) return null
  const next = `/auth/hand-off?return=${encodeURIComponent(ret)}`
  return `${HUB_SIGNUP_URL}?intent=${intent}&next=${encodeURIComponent(next)}`
}

/** Hub sign-up URL returning to `path` on the page's own origin, or null (switch off / not an own https host). */
export function hubSignupHref(path: string): string | null {
  if (!hubSignupEnabled() || typeof window === 'undefined') return null
  return hubSignupUrl({ returnUrl: `${window.location.origin}${path}` })
}
